import { useEffect } from 'react';

import dayjs from 'dayjs';

import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import type { TypeGetWorkApplicationsListParams, TypeGetWorkApplicationsListResponse } from '~/api/workApplications';
import { registerMockAPI } from '~/mocks/mockApis';

import { axiosInstance } from '~/services/fetchServiceV2';
import { useLoadingStore } from '~/store';
import { formatDate } from '~/utils';

const PAGE_SIZE = 10;

/** @description 지원내역 목록 조회 */
export const useGetWorkApplyList = ({ workDateFrom, workDateTo }: TypeGetWorkApplicationsListParams) => {
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch } = useSuspenseInfiniteQuery({
    queryKey: ['work-apply-list', workDateFrom, workDateTo],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      return await axiosInstance.get<TypeGetWorkApplicationsListResponse>('/work-applications', {
        params: { workDateFrom, workDateTo, page: pageParam, size: PAGE_SIZE },
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { hasNext, pageRequest } = lastPage.data.data;
      return hasNext ? pageRequest.page + 1 : undefined;
    },
    select: (infiniteData) => infiniteData.pages.flatMap((page) => page.data.data.content ?? []),
    gcTime: 0,
    retry: 1,
  });

  const setLoading = useLoadingStore((state) => state.actions.setLoading);

  useEffect(() => {
    setLoading({ show: isFetching && !isFetchingNextPage, dimmed: true });
  }, [isFetching, isFetchingNextPage]);

  return { data: data ?? [], fetchNextPage, hasNextPage, isFetchingNextPage, refetch };
};

registerMockAPI<TypeGetWorkApplicationsListResponse>({
  method: 'get',
  endpoint: '/work-applications',
  label: '지원내역/목록 조회',
  scenarios: [
    {
      name: '토큰 만료',
      status: 401,
      response: {
        code: 'AUTH_GUEST',
        message: '토큰이 만료되었습니다.',
        data: null,
      } as unknown as TypeGetWorkApplicationsListResponse,
    },
    {
      name: '목록 있음 (3페이지 / 30건)',
      responses: [
        // 1페이지
        {
          delay: 600,
          response: {
            code: '0000',
            message: '정상',
            data: {
              isFirst: true,
              total: 30,
              hasContent: true,
              isLast: false,
              totalPages: 3,
              pageRequest: { offset: 0, size: 10, page: 1 },
              hasPrevious: false,
              hasNext: true,
              content: [
                {
                  tid: 'tid-001',
                  workLocationName: '김포',
                  applicationDate: '2026-01-20',
                  workDate: '2026-01-25',
                  task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                  applications: [
                    {
                      workApplicationId: '1',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                    {
                      workApplicationId: '2',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-002',
                  workLocationName: '평택',
                  applicationDate: '2026-01-18',
                  workDate: '2026-01-22',
                  task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
                  applications: [
                    {
                      workApplicationId: '3',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-003',
                  workLocationName: '안산',
                  applicationDate: '2026-01-15',
                  workDate: '2026-01-18',
                  task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
                  applications: [
                    {
                      workApplicationId: '4',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '16:00 ~ 00:50',
                        startTime: '16:00',
                        endTime: '00:50',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                    {
                      workApplicationId: '5',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                  ],
                },
                {
                  tid: 'tid-004',
                  workLocationName: '송파',
                  applicationDate: '2026-01-12',
                  workDate: '2026-01-15',
                  task: { code: 'QC', text: '품질관리', description: '품질관리 작업' },
                  applications: [
                    {
                      workApplicationId: '6',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '08:00 ~ 17:00',
                        startTime: '08:00',
                        endTime: '17:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
                    },
                  ],
                },
                {
                  tid: 'tid-005',
                  workLocationName: '군포',
                  applicationDate: '2026-01-10',
                  workDate: '2026-01-13',
                  task: { code: 'INVENTORY', text: '재고관리', description: '재고관리 작업' },
                  applications: [
                    {
                      workApplicationId: '7',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                    {
                      workApplicationId: '8',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '17:00 ~ 02:00',
                        startTime: '17:00',
                        endTime: '02:00',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-006',
                  workLocationName: '김포',
                  applicationDate: '2026-01-08',
                  workDate: '2026-01-11',
                  task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                  applications: [
                    {
                      workApplicationId: '9',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-007',
                  workLocationName: '평택',
                  applicationDate: '2026-01-05',
                  workDate: '2026-01-08',
                  task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
                  applications: [
                    {
                      workApplicationId: '10',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '16:00 ~ 00:50',
                        startTime: '16:00',
                        endTime: '00:50',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                    {
                      workApplicationId: '11',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                  ],
                },
                {
                  tid: 'tid-008',
                  workLocationName: '안산',
                  applicationDate: '2026-01-03',
                  workDate: '2026-01-05',
                  task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
                  applications: [
                    {
                      workApplicationId: '12',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '08:00 ~ 17:00',
                        startTime: '08:00',
                        endTime: '17:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
                    },
                  ],
                },
                {
                  tid: 'tid-009',
                  workLocationName: '송파',
                  applicationDate: '2025-12-30',
                  workDate: '2026-01-02',
                  task: { code: 'QC', text: '품질관리', description: '품질관리 작업' },
                  applications: [
                    {
                      workApplicationId: '13',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                    {
                      workApplicationId: '14',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-010',
                  workLocationName: '군포',
                  applicationDate: '2025-12-28',
                  workDate: '2025-12-31',
                  task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                  applications: [
                    {
                      workApplicationId: '15',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
              ],
            },
          } satisfies TypeGetWorkApplicationsListResponse,
        },
        // 2페이지
        {
          delay: 1000,
          response: {
            code: '0000',
            message: '정상',
            data: {
              isFirst: false,
              total: 30,
              hasContent: true,
              isLast: false,
              totalPages: 3,
              pageRequest: { offset: 10, size: 10, page: 2 },
              hasPrevious: true,
              hasNext: true,
              content: [
                {
                  tid: 'tid-011',
                  workLocationName: '김포',
                  applicationDate: '2025-12-25',
                  workDate: '2025-12-28',
                  task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
                  applications: [
                    {
                      workApplicationId: '16',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                    {
                      workApplicationId: '17',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '16:00 ~ 00:50',
                        startTime: '16:00',
                        endTime: '00:50',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                  ],
                },
                {
                  tid: 'tid-012',
                  workLocationName: '평택',
                  applicationDate: '2025-12-20',
                  workDate: '2025-12-23',
                  task: { code: 'INVENTORY', text: '재고관리', description: '재고관리 작업' },
                  applications: [
                    {
                      workApplicationId: '18',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '08:00 ~ 17:00',
                        startTime: '08:00',
                        endTime: '17:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
                    },
                  ],
                },
                {
                  tid: 'tid-013',
                  workLocationName: '안산',
                  applicationDate: '2025-11-18',
                  workDate: '2025-11-21',
                  task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
                  applications: [
                    {
                      workApplicationId: '19',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                    {
                      workApplicationId: '20',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                  ],
                },
                {
                  tid: 'tid-014',
                  workLocationName: '송파',
                  applicationDate: '2025-10-10',
                  workDate: '2025-10-14',
                  task: { code: 'QC', text: '품질관리', description: '품질관리 작업' },
                  applications: [
                    {
                      workApplicationId: '21',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '16:00 ~ 00:50',
                        startTime: '16:00',
                        endTime: '00:50',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                  ],
                },
                {
                  tid: 'tid-015',
                  workLocationName: '군포',
                  applicationDate: '2025-09-05',
                  workDate: '2025-09-08',
                  task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                  applications: [
                    {
                      workApplicationId: '22',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                    {
                      workApplicationId: '23',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
                    },
                  ],
                },
                {
                  tid: 'tid-016',
                  workLocationName: '김포',
                  applicationDate: '2025-08-15',
                  workDate: '2025-08-18',
                  task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
                  applications: [
                    {
                      workApplicationId: '24',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '08:00 ~ 17:00',
                        startTime: '08:00',
                        endTime: '17:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-017',
                  workLocationName: '평택',
                  applicationDate: '2025-07-20',
                  workDate: '2025-07-24',
                  task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
                  applications: [
                    {
                      workApplicationId: '25',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                    {
                      workApplicationId: '26',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '17:00 ~ 02:00',
                        startTime: '17:00',
                        endTime: '02:00',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                  ],
                },
                {
                  tid: 'tid-018',
                  workLocationName: '안산',
                  applicationDate: '2025-06-08',
                  workDate: '2025-06-11',
                  task: { code: 'QC', text: '품질관리', description: '품질관리 작업' },
                  applications: [
                    {
                      workApplicationId: '27',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-019',
                  workLocationName: '송파',
                  applicationDate: '2025-05-02',
                  workDate: '2025-05-05',
                  task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                  applications: [
                    {
                      workApplicationId: '28',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '16:00 ~ 00:50',
                        startTime: '16:00',
                        endTime: '00:50',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
                    },
                    {
                      workApplicationId: '29',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                  ],
                },
                {
                  tid: 'tid-020',
                  workLocationName: '군포',
                  applicationDate: '2025-04-10',
                  workDate: '2025-04-14',
                  task: { code: 'INVENTORY', text: '재고관리', description: '재고관리 작업' },
                  applications: [
                    {
                      workApplicationId: '30',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '08:00 ~ 17:00',
                        startTime: '08:00',
                        endTime: '17:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
              ],
            },
          } satisfies TypeGetWorkApplicationsListResponse,
        },
        // 3페이지 (마지막)
        {
          delay: 1000,
          response: {
            code: '0000',
            message: '정상',
            data: {
              isFirst: false,
              total: 30,
              hasContent: true,
              isLast: true,
              totalPages: 3,
              pageRequest: { offset: 20, size: 10, page: 3 },
              hasPrevious: true,
              hasNext: false,
              content: [
                {
                  tid: 'tid-021',
                  workLocationName: '김포',
                  applicationDate: '2025-03-05',
                  workDate: '2025-03-08',
                  task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
                  applications: [
                    {
                      workApplicationId: '31',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                    {
                      workApplicationId: '32',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                  ],
                },
                {
                  tid: 'tid-022',
                  workLocationName: '평택',
                  applicationDate: '2025-02-12',
                  workDate: '2025-02-15',
                  task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
                  applications: [
                    {
                      workApplicationId: '33',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-023',
                  workLocationName: '안산',
                  applicationDate: '2025-01-18',
                  workDate: '2025-01-22',
                  task: { code: 'QC', text: '품질관리', description: '품질관리 작업' },
                  applications: [
                    {
                      workApplicationId: '34',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '16:00 ~ 00:50',
                        startTime: '16:00',
                        endTime: '00:50',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
                    },
                    {
                      workApplicationId: '35',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                  ],
                },
                {
                  tid: 'tid-024',
                  workLocationName: '송파',
                  applicationDate: '2024-12-10',
                  workDate: '2024-12-14',
                  task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                  applications: [
                    {
                      workApplicationId: '36',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '08:00 ~ 17:00',
                        startTime: '08:00',
                        endTime: '17:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-025',
                  workLocationName: '군포',
                  applicationDate: '2024-11-03',
                  workDate: '2024-11-07',
                  task: { code: 'INVENTORY', text: '재고관리', description: '재고관리 작업' },
                  applications: [
                    {
                      workApplicationId: '37',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                    {
                      workApplicationId: '38',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '17:00 ~ 02:00',
                        startTime: '17:00',
                        endTime: '02:00',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                  ],
                },
                {
                  tid: 'tid-026',
                  workLocationName: '김포',
                  applicationDate: '2024-10-08',
                  workDate: '2024-10-12',
                  task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
                  applications: [
                    {
                      workApplicationId: '39',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-027',
                  workLocationName: '평택',
                  applicationDate: '2024-09-14',
                  workDate: '2024-09-18',
                  task: { code: 'QC', text: '품질관리', description: '품질관리 작업' },
                  applications: [
                    {
                      workApplicationId: '40',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
                    },
                    {
                      workApplicationId: '41',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '16:00 ~ 00:50',
                        startTime: '16:00',
                        endTime: '00:50',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                  ],
                },
                {
                  tid: 'tid-028',
                  workLocationName: '안산',
                  applicationDate: '2024-08-20',
                  workDate: '2024-08-24',
                  task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
                  applications: [
                    {
                      workApplicationId: '42',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '08:00 ~ 17:00',
                        startTime: '08:00',
                        endTime: '17:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-029',
                  workLocationName: '송파',
                  applicationDate: '2024-07-05',
                  workDate: '2024-07-09',
                  task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                  applications: [
                    {
                      workApplicationId: '43',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                    {
                      workApplicationId: '44',
                      priority: { code: 'SECOND', text: '2지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                  ],
                },
                {
                  tid: 'tid-030',
                  workLocationName: '군포',
                  applicationDate: '2024-06-10',
                  workDate: '2024-06-14',
                  task: { code: 'INVENTORY', text: '재고관리', description: '재고관리 작업' },
                  applications: [
                    {
                      workApplicationId: '45',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
              ],
            },
          } satisfies TypeGetWorkApplicationsListResponse,
        },
      ],
    },
    {
      name: '목록 있음 (1페이지 / 4건)',
      response: {
        code: '0000',
        message: '정상',
        data: {
          isFirst: true,
          total: 4,
          hasContent: true,
          isLast: true,
          totalPages: 1,
          pageRequest: { offset: 0, size: 10, page: 1 },
          hasPrevious: false,
          hasNext: false,
          content: [
            {
              tid: 'tid-s01',
              workLocationName: '김포',
              applicationDate: '2026-01-20',
              workDate: '2026-01-25',
              task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
              applications: [
                {
                  workApplicationId: '101',
                  priority: { code: 'FIRST', text: '1지망' },
                  workShift: {
                    formattedText: '13:00 ~ 22:00',
                    startTime: '13:00',
                    endTime: '22:00',
                    isStartTimeBeforeEndTime: true,
                  },
                  workEnvironment: { code: 'COLD', text: '냉장' },
                },
                {
                  workApplicationId: '102',
                  priority: { code: 'SECOND', text: '2지망' },
                  workShift: {
                    formattedText: '09:00 ~ 18:00',
                    startTime: '09:00',
                    endTime: '18:00',
                    isStartTimeBeforeEndTime: true,
                  },
                  workEnvironment: { code: 'NORMAL', text: '상온' },
                },
              ],
            },
            {
              tid: 'tid-s02',
              workLocationName: '평택',
              applicationDate: '2026-01-10',
              workDate: '2026-01-14',
              task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
              applications: [
                {
                  workApplicationId: '103',
                  priority: { code: 'FIRST', text: '1지망' },
                  workShift: {
                    formattedText: '09:00 ~ 18:00',
                    startTime: '09:00',
                    endTime: '18:00',
                    isStartTimeBeforeEndTime: true,
                  },
                  workEnvironment: { code: 'NORMAL', text: '상온' },
                },
              ],
            },
            {
              tid: 'tid-s03',
              workLocationName: '안산',
              applicationDate: '2025-12-22',
              workDate: '2025-12-26',
              task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
              applications: [
                {
                  workApplicationId: '104',
                  priority: { code: 'FIRST', text: '1지망' },
                  workShift: {
                    formattedText: '16:00 ~ 00:50',
                    startTime: '16:00',
                    endTime: '00:50',
                    isStartTimeBeforeEndTime: false,
                  },
                  workEnvironment: { code: 'HUB', text: 'HUB' },
                },
              ],
            },
            {
              tid: 'tid-s04',
              workLocationName: '송파',
              applicationDate: '2025-11-15',
              workDate: '2025-11-18',
              task: { code: 'QC', text: '품질관리', description: '품질관리 작업' },
              applications: [
                {
                  workApplicationId: '105',
                  priority: { code: 'FIRST', text: '1지망' },
                  workShift: {
                    formattedText: '13:00 ~ 22:00',
                    startTime: '13:00',
                    endTime: '22:00',
                    isStartTimeBeforeEndTime: true,
                  },
                  workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
                },
                {
                  workApplicationId: '106',
                  priority: { code: 'SECOND', text: '2지망' },
                  workShift: {
                    formattedText: '08:00 ~ 17:00',
                    startTime: '08:00',
                    endTime: '17:00',
                    isStartTimeBeforeEndTime: true,
                  },
                  workEnvironment: { code: 'COLD', text: '냉장' },
                },
              ],
            },
          ],
        },
      } satisfies TypeGetWorkApplicationsListResponse,
    },
    {
      name: '목록 있음 (2페이지 / 10초 딜레이)',
      responses: [
        {
          delay: 600,
          response: {
            code: '0000',
            message: '정상',
            data: {
              isFirst: true,
              total: 20,
              hasContent: true,
              isLast: false,
              totalPages: 2,
              pageRequest: { offset: 0, size: 10, page: 1 },
              hasPrevious: false,
              hasNext: true,
              content: [
                {
                  tid: 'tid-delay-01',
                  workLocationName: '김포',
                  applicationDate: '2026-01-20',
                  workDate: '2026-01-25',
                  task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                  applications: [
                    {
                      workApplicationId: '201',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                  ],
                },
                {
                  tid: 'tid-delay-02',
                  workLocationName: '평택',
                  applicationDate: '2026-01-18',
                  workDate: '2026-01-22',
                  task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
                  applications: [
                    {
                      workApplicationId: '202',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-delay-03',
                  workLocationName: '안산',
                  applicationDate: '2026-01-15',
                  workDate: '2026-01-18',
                  task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
                  applications: [
                    {
                      workApplicationId: '203',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '16:00 ~ 00:50',
                        startTime: '16:00',
                        endTime: '00:50',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                  ],
                },
                {
                  tid: 'tid-delay-04',
                  workLocationName: '송파',
                  applicationDate: '2026-01-12',
                  workDate: '2026-01-15',
                  task: { code: 'QC', text: '품질관리', description: '품질관리 작업' },
                  applications: [
                    {
                      workApplicationId: '204',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '08:00 ~ 17:00',
                        startTime: '08:00',
                        endTime: '17:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
                    },
                  ],
                },
                {
                  tid: 'tid-delay-05',
                  workLocationName: '군포',
                  applicationDate: '2026-01-10',
                  workDate: '2026-01-13',
                  task: { code: 'INVENTORY', text: '재고관리', description: '재고관리 작업' },
                  applications: [
                    {
                      workApplicationId: '205',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                  ],
                },
                {
                  tid: 'tid-delay-06',
                  workLocationName: '김포',
                  applicationDate: '2026-01-08',
                  workDate: '2026-01-11',
                  task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                  applications: [
                    {
                      workApplicationId: '206',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-delay-07',
                  workLocationName: '평택',
                  applicationDate: '2026-01-05',
                  workDate: '2026-01-08',
                  task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
                  applications: [
                    {
                      workApplicationId: '207',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '16:00 ~ 00:50',
                        startTime: '16:00',
                        endTime: '00:50',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                  ],
                },
                {
                  tid: 'tid-delay-08',
                  workLocationName: '안산',
                  applicationDate: '2026-01-03',
                  workDate: '2026-01-05',
                  task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
                  applications: [
                    {
                      workApplicationId: '208',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '08:00 ~ 17:00',
                        startTime: '08:00',
                        endTime: '17:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
                {
                  tid: 'tid-delay-09',
                  workLocationName: '송파',
                  applicationDate: '2025-12-30',
                  workDate: '2026-01-02',
                  task: { code: 'QC', text: '품질관리', description: '품질관리 작업' },
                  applications: [
                    {
                      workApplicationId: '209',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '13:00 ~ 22:00',
                        startTime: '13:00',
                        endTime: '22:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'COLD', text: '냉장' },
                    },
                  ],
                },
                {
                  tid: 'tid-delay-10',
                  workLocationName: '군포',
                  applicationDate: '2025-12-28',
                  workDate: '2025-12-31',
                  task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                  applications: [
                    {
                      workApplicationId: '210',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '09:00 ~ 18:00',
                        startTime: '09:00',
                        endTime: '18:00',
                        isStartTimeBeforeEndTime: true,
                      },
                      workEnvironment: { code: 'NORMAL', text: '상온' },
                    },
                  ],
                },
              ],
            },
          } satisfies TypeGetWorkApplicationsListResponse,
        },
        {
          delay: 10000,
          response: {
            code: '0000',
            message: '정상',
            data: {
              isFirst: false,
              total: 20,
              hasContent: true,
              isLast: true,
              totalPages: 2,
              pageRequest: { offset: 10, size: 10, page: 2 },
              hasPrevious: true,
              hasNext: false,
              content: [
                {
                  tid: 'tid-delay-11',
                  workLocationName: '안산',
                  applicationDate: '2025-12-25',
                  workDate: '2025-12-28',
                  task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
                  applications: [
                    {
                      workApplicationId: '211',
                      priority: { code: 'FIRST', text: '1지망' },
                      workShift: {
                        formattedText: '16:00 ~ 00:50',
                        startTime: '16:00',
                        endTime: '00:50',
                        isStartTimeBeforeEndTime: false,
                      },
                      workEnvironment: { code: 'HUB', text: 'HUB' },
                    },
                  ],
                },
              ],
            },
          } satisfies TypeGetWorkApplicationsListResponse,
        },
      ],
    },
    {
      name: '목록 있음 (1페이지 / 4건 / 실제 시간)',
      get response() {
        const today = dayjs();
        return {
          code: '0000',
          message: '정상',
          data: {
            isFirst: true,
            total: 4,
            hasContent: true,
            isLast: true,
            totalPages: 1,
            pageRequest: { offset: 0, size: 10, page: 1 },
            hasPrevious: false,
            hasNext: false,
            content: [
              {
                tid: 'tid-rt-001',
                workLocationName: '김포',
                applicationDate: formatDate({ value: today.subtract(1, 'day') }),
                workDate: formatDate({ value: today.add(3, 'day') }),
                task: { code: 'MID_SORT', text: '중간분류', description: '중간분류 작업' },
                applications: [
                  {
                    workApplicationId: '1',
                    priority: { code: 'FIRST', text: '1지망' },
                    workShift: {
                      formattedText: '13:00 ~ 22:00',
                      startTime: '13:00',
                      endTime: '22:00',
                      isStartTimeBeforeEndTime: true,
                    },
                    workEnvironment: { code: 'COLD', text: '냉장' },
                  },
                  {
                    workApplicationId: '1',
                    priority: { code: 'SECOND', text: '2지망' },
                    workShift: {
                      formattedText: '09:00 ~ 18:00',
                      startTime: '09:00',
                      endTime: '18:00',
                      isStartTimeBeforeEndTime: true,
                    },
                    workEnvironment: { code: 'NORMAL', text: '상온' },
                  },
                ],
              },
              {
                tid: 'tid-rt-002',
                workLocationName: '평택',
                applicationDate: formatDate({ value: today.subtract(3, 'day') }),
                workDate: formatDate({ value: today.add(1, 'day') }),
                task: { code: 'OUTBOUND', text: '출고', description: '출고 작업' },
                applications: [
                  {
                    workApplicationId: '2',
                    priority: { code: 'FIRST', text: '1지망' },
                    workShift: {
                      formattedText: '09:00 ~ 18:00',
                      startTime: '09:00',
                      endTime: '18:00',
                      isStartTimeBeforeEndTime: true,
                    },
                    workEnvironment: { code: 'NORMAL', text: '상온' },
                  },
                ],
              },
              {
                tid: 'tid-rt-003',
                workLocationName: '안산',
                applicationDate: formatDate({ value: today.subtract(5, 'day') }),
                workDate: formatDate({ value: today.subtract(1, 'day') }),
                task: { code: 'INBOUND', text: '입고', description: '입고 작업' },
                applications: [
                  {
                    workApplicationId: '3',
                    priority: { code: 'FIRST', text: '1지망' },
                    workShift: {
                      formattedText: '16:00 ~ 00:50',
                      startTime: '16:00',
                      endTime: '00:50',
                      isStartTimeBeforeEndTime: false,
                    },
                    workEnvironment: { code: 'HUB', text: 'HUB' },
                  },
                ],
              },
              {
                tid: 'tid-rt-004',
                workLocationName: '송파',
                applicationDate: formatDate({ value: today.subtract(7, 'day') }),
                workDate: formatDate({ value: today.subtract(3, 'day') }),
                task: { code: 'QC', text: '품질관리', description: '품질관리 작업' },
                applications: [
                  {
                    workApplicationId: '4',
                    priority: { code: 'FIRST', text: '1지망' },
                    workShift: {
                      formattedText: '13:00 ~ 22:00',
                      startTime: '13:00',
                      endTime: '22:00',
                      isStartTimeBeforeEndTime: true,
                    },
                    workEnvironment: { code: 'ALL_TEMP', text: '전체온도' },
                  },
                  {
                    workApplicationId: '4',
                    priority: { code: 'SECOND', text: '2지망' },
                    workShift: {
                      formattedText: '08:00 ~ 17:00',
                      startTime: '08:00',
                      endTime: '17:00',
                      isStartTimeBeforeEndTime: true,
                    },
                    workEnvironment: { code: 'COLD', text: '냉장' },
                  },
                ],
              },
            ],
          },
        } satisfies TypeGetWorkApplicationsListResponse;
      },
    },
    {
      name: '목록 없음',
      response: {
        code: '0000',
        message: '정상',
        data: {
          isFirst: true,
          total: 0,
          hasContent: false,
          isLast: true,
          totalPages: 0,
          pageRequest: { offset: 0, size: 10, page: 1 },
          hasPrevious: false,
          hasNext: false,
          content: [],
        },
      } satisfies TypeGetWorkApplicationsListResponse,
    },
  ],
});
